import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { CheckCircle2, Calendar, Repeat } from "lucide-react";
import RevealText from "./RevealText";

/**
 * BillingTransparency — no-contract billing flow in three editorial steps.
 *
 * Visual language matches `ThreeStepFlow.jsx`: big circular green-tint icon
 * badges with a soft radial halo, kicker label above the title, dashed
 * connectors between steps on desktop, staggered reveal on scroll.
 */

function StepIcon({ Icon }) {
  return (
    <div className="relative grid place-items-center">
      <span
        aria-hidden
        className="absolute rounded-full"
        style={{
          width: 132,
          height: 132,
          background:
            "radial-gradient(closest-side, rgba(47,122,58,0.16) 0%, rgba(47,122,58,0.06) 60%, rgba(47,122,58,0) 100%)",
        }}
      />
      <span
        aria-hidden
        className="absolute rounded-full"
        style={{ width: 96, height: 96, background: "rgba(47,122,58,0.10)" }}
      />
      <span className="relative grid place-items-center">
        <Icon className="w-10 h-10 text-velcor-green" strokeWidth={1.7} />
      </span>
    </div>
  );
}

export default function BillingTransparency() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "es";

  const titleLead = lang === "en" ? "Free activation." : "Activación gratis.";
  const titleAccent = lang === "en" ? "No contracts." : "Sin contratos.";
  const intro =
    lang === "en"
      ? "We keep your costs predictable and transparent. Cancel any time — no early termination fee, no surprises."
      : "Mantenemos tus costos predecibles y transparentes. Cancela cuando quieras, sin fee por terminación anticipada ni sorpresas.";

  const steps =
    lang === "en"
      ? [
          {
            icon: CheckCircle2,
            kicker: "Day 0",
            title: "Free activation",
            body: "We turn your service on with no setup fee, no deposit and no long-term contract.",
          },
          {
            icon: Calendar,
            kicker: "Day 7–10",
            title: "First prorated payment",
            body: "A variable charge between $40–$70, calculated based on your home's estimated usage.",
          },
          {
            icon: Repeat,
            kicker: "Day 30+",
            title: "Fixed monthly billing",
            body: "From here on, your bill is locked at the same price every month. No surprises.",
          },
        ]
      : [
          {
            icon: CheckCircle2,
            kicker: "Día 0",
            title: "Activación gratis",
            body: "Encendemos tu servicio sin costo de instalación, sin depósito y sin contrato a largo plazo.",
          },
          {
            icon: Calendar,
            kicker: "Día 7–10",
            title: "Primer pago prorrateado",
            body: "Un cargo variable entre $40 y $70, calculado según el consumo estimado de tu hogar.",
          },
          {
            icon: Repeat,
            kicker: "Día 30+",
            title: "Facturación mensual fija",
            body: "A partir de aquí, tu factura queda fija al mismo precio cada mes. Sin sorpresas.",
          },
        ];

  return (
    <section
      id="how-billing-works"
      className="relative max-w-[1180px] mx-auto px-5 py-16 md:py-20"
      data-testid="billing-transparency"
    >
      <div className="text-center max-w-[920px] mx-auto mb-14 md:mb-20">
        <div className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-velcor-green mb-2.5">
          {lang === "en" ? "How billing works" : "Cómo funciona la facturación"}
        </div>
        <h2
          className="display text-velcor-ink leading-[1.06]"
          style={{ fontSize: "clamp(28px, 4.6vw, 50px)", fontWeight: 600, letterSpacing: "-0.02em" }}
        >
          <RevealText text={titleLead} />{" "}
          <RevealText text={titleAccent} className="display-accent" delay={0.18} />
        </h2>
        <p className="text-slate-500 text-[14.5px] mt-4 max-w-[640px] mx-auto leading-relaxed">
          {intro}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr] items-start md:items-center gap-y-10 md:gap-y-0 gap-x-0 md:gap-x-4">
        {steps.map((s, idx) => (
          <React.Fragment key={s.kicker}>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: idx * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-col items-center text-center cursor-default"
              data-testid={`billing-step-${idx + 1}`}
            >
              <StepIcon Icon={s.icon} />
              <div className="mt-7 md:mt-10 inline-flex items-center px-2.5 py-0.5 rounded-md bg-velcor-ink text-white text-[10.5px] font-extrabold uppercase tracking-[0.14em] tabular-nums">
                {s.kicker}
              </div>
              <h3
                className="mt-3 text-velcor-ink tracking-tight"
                style={{
                  fontFamily: "Inter, ui-sans-serif, system-ui, -apple-system, sans-serif",
                  fontSize: 18,
                  fontWeight: 700,
                }}
              >
                {s.title}
              </h3>
              <p
                className="mt-3 text-slate-500 leading-relaxed max-w-[280px]"
                style={{ fontSize: 14, fontWeight: 400 }}
              >
                {s.body}
              </p>
            </motion.div>

            {idx < steps.length - 1 && (
              <motion.div
                aria-hidden
                initial={{ scaleX: 0, opacity: 0 }}
                whileInView={{ scaleX: 1, opacity: 1 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: idx * 0.12 + 0.25, ease: "easeOut" }}
                className="hidden md:flex items-center justify-center origin-left"
                style={{ minWidth: 60, marginTop: -110 }}
              >
                <span
                  className="block w-full"
                  style={{
                    height: 0,
                    borderTop: "2px dashed rgba(15,23,42,0.18)",
                    minWidth: 80,
                  }}
                />
              </motion.div>
            )}
          </React.Fragment>
        ))}
      </div>

      <div className="mt-10 md:mt-14 text-center">
        <p className="text-[12.5px] text-slate-400 leading-relaxed max-w-[680px] mx-auto">
          {lang === "en"
            ? "* The first payment is calculated using ESIID-based meter data for your specific address. Prices vary slightly by ZIP code due to local utility (CenterPoint, Oncor, AEP, TNMP) delivery costs."
            : "* El primer pago se calcula con datos del medidor (ESIID) para tu dirección específica. Los precios varían ligeramente por código postal debido a los costos de entrega del utility local (CenterPoint, Oncor, AEP, TNMP)."}
        </p>
      </div>
    </section>
  );
}
